import { type FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MdAdd, 
  MdMenu, 
  MdNotifications,
  MdNotificationsOff,
  MdPerson, 
  MdArchive, 
  MdCalendarToday,
  MdClose,
  MdViewList,
  MdViewModule,
  MdViewKanban,
  MdSearch
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useNotifications } from '../hooks/useNotifications';
import { useTasksWithRealtime } from '../hooks/useTasksWithRealtime';
import { useProfile } from '../hooks/useProfile';
import type { Task, ViewMode } from '../types';
import { archiveTask } from '../services/api';
import TaskForm from '../components/TaskForm';
import KanbanBoard from '../components/KanbanBoard';
import ThemeToggle from '../components/ThemeToggle';
import { DashboardSkeleton } from '../components/Skeleton';
import TaskCard from '../components/TaskCard';
import ConnectionStatus from '../components/ConnectionStatus';

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { profile } = useProfile(user?.id);
  
  // Use enhanced hooks with realtime and caching
  const { tasks, loading, addTask, editTask, removeTask } = useTasksWithRealtime({
    enableRealtime: true,
    autoRefetch: false, // Realtime handles updates
  });
  
  const { 
    supported: notificationsSupported,
    permission: notificationPermission,
    requestPermission,
    scheduleAllTaskNotifications,
  } = useNotifications();
  
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Track if notifications are enabled (separate from permission)
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('taskapp_notifications_enabled');
    return saved !== null ? saved === 'true' : true; // Default to true
  });
  
  // Load viewMode from localStorage, default to 'list'
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('taskapp_viewMode');
    return (saved as ViewMode) || 'list';
  });
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Track scroll position for navbar blur
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Save viewMode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('taskapp_viewMode', viewMode);
  }, [viewMode]);
  
  // Save notification preferences
  useEffect(() => {
    localStorage.setItem('taskapp_notifications_enabled', notificationsEnabled.toString());
  }, [notificationsEnabled]);

  // Request notification permission and schedule notifications
  useEffect(() => {
    if (notificationsSupported && notificationPermission === 'default') {
      // Show a prompt to request permissions
      toast.info('Enable notifications to get task reminders!', {
        onClick: () => requestPermission(),
        autoClose: 5000,
      });
    }
  }, [notificationsSupported, notificationPermission, requestPermission]);

  // Schedule notifications when tasks change
  useEffect(() => {
    // Only schedule notifications if both permission granted and notifications enabled
    if (notificationPermission === 'granted' && notificationsEnabled && tasks.length > 0) {
      console.log('[Dashboard] Scheduling notifications for', tasks.length, 'tasks');
      scheduleAllTaskNotifications(tasks);
    }
  }, [tasks, notificationPermission, notificationsEnabled, scheduleAllTaskNotifications]);
  
  // Function to toggle notifications
  const toggleNotifications = useCallback(async () => {
    // If permission not granted, request it first
    if (notificationPermission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) {
        toast.error('Please allow notifications in your browser settings');
        return;
      }
    }
    
    // Toggle enabled state
    setNotificationsEnabled(prev => !prev);
    // Note: No toast notification shown to avoid double notification issue
  }, [notificationPermission, requestPermission]);

  const handleCreateTask = async (taskData: Partial<Task>) => {
    try {
      // Ensure required fields for CreateTaskInput
      if (!taskData.title) {
        toast.error('Task title is required');
        return;
      }
      
      await addTask({
        title: taskData.title,
        description: taskData.description,
        due_date: taskData.due_date,
        status: taskData.status || 'pending',
        priority: taskData.priority || 'medium',
        start_time: taskData.start_time,
        alarm_time: taskData.alarm_time,
        tags: taskData.tags,
        reminder_interval: taskData.reminder_interval
      });
      
      setShowForm(false);
      toast.success('Task created');
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskData: Partial<Task>) => {
    if (!editingTask) return;

    try {
      await editTask(editingTask.id, {
        title: taskData.title,
        description: taskData.description,
        due_date: taskData.due_date,
        status: taskData.status,
        priority: taskData.priority,
        start_time: taskData.start_time,
        alarm_time: taskData.alarm_time,
        tags: taskData.tags,
        reminder_interval: taskData.reminder_interval
      });
      
      setEditingTask(null);
      setShowForm(false);
      // Only show toast for major updates
      toast.success('Task updated');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const handleDelete = async (taskId: string) => {
    if (!confirm('Delete this task?')) return;

    try {
      await removeTask(taskId);
      toast.success('Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleArchive = async (taskId: string) => {
    try {
      await archiveTask(taskId);
      // Task will be removed from list via realtime subscription
      toast.success('Task archived');
    } catch (error) {
      console.error('Failed to archive task:', error);
      toast.error('Failed to archive task');
    }
  };

  const handleUpdateStatus = async (taskId: string, newStatus: 'pending' | 'in-progress' | 'completed') => {
    try {
      await editTask(taskId, { status: newStatus });
      // No toast notification for status changes - too noisy
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingTask(null);
  };

  // Filter tasks based on search query
  const filteredTasks = useMemo(() => {
    if (!searchQuery.trim()) return tasks;
    
    const query = searchQuery.toLowerCase();
    return tasks.filter(task => 
      task.title.toLowerCase().includes(query) ||
      task.description?.toLowerCase().includes(query) ||
      task.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }, [tasks, searchQuery]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Get accent color with opacity for gradient
  const getAccentColor = () => {
    const colors: Record<string, string> = {
      blue: '0, 255, 255',
      green: '0, 255, 0',
      purple: '139, 0, 255',
      pink: '255, 0, 255',
      orange: '255, 107, 0',
      yellow: '255, 255, 0'
    };
    return colors[document.documentElement.style.getPropertyValue('--color-theme-name')?.trim() || 'blue'];
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? `linear-gradient(135deg, rgba(17, 24, 39, 1) 0%, rgba(${getAccentColor()}, 0.03) 100%)`
          : `linear-gradient(135deg, rgba(240, 244, 248, 1) 0%, rgba(${getAccentColor()}, 0.05) 100%)`
      }}
    >
      {/* Connection Status Indicator */}
      <ConnectionStatus />

      {/* Top Navigation */}
            {/* Top Navigation */}
      <nav className={`
        dark:bg-dark-surface/95
        bg-light-surface/95
        border-b-2
        dark:border-neon-blue
        border-blue-400
        shadow-lg
        sticky
        top-0
        z-50
        transition-all
        duration-300
        ${scrolled ? 'backdrop-blur-xl bg-light-surface/80 dark:bg-dark-surface/80' : 'backdrop-blur-sm'}
      `}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/kanban-icon.svg" 
                alt="Taskaban Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10"
              />
              <span className="text-base sm:text-xl font-bold hidden sm:block" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                Taskaban
              </span>
            </div>

            {/* Desktop Navigation */}
                        {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2 lg:gap-4">
              {/* Calendar Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/calendar')}
                className="
                  flex items-center gap-2
                  px-3 py-2
                  rounded-lg
                  dark:text-gray-300
                  text-gray-700
                  hover:dark:text-theme
                  hover:text-theme
                  transition-colors
                "
              >
                <MdCalendarToday className="w-4 h-4 lg:w-5 lg:h-5" />
                <span className="hidden lg:inline">Calendar</span>
              </motion.button>

              {/* Archive Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/archive')}
                className="
                  flex items-center gap-2
                  px-3 py-2
                  rounded-lg
                  dark:text-gray-300
                  text-gray-700
                  hover:dark:text-theme
                  hover:text-theme
                  transition-colors
                "
              >
                <MdArchive className="w-4 h-4 lg:w-5 lg:h-5" />
                <span className="hidden lg:inline">Archive</span>
              </motion.button>

              {/* Notifications Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleNotifications}
                className="
                  relative
                  flex items-center gap-2
                  px-3 py-2
                  rounded-lg
                  transition-colors
                "
                style={{
                  backgroundColor: notificationsEnabled 
                    ? (theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.1)')
                    : 'transparent',
                  color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                }}
                title={notificationsEnabled ? 'Notifications Enabled' : 'Notifications Disabled'}
              >
                {notificationsEnabled ? (
                  <MdNotifications className="w-5 h-5 lg:w-6 lg:h-6 text-neon-green dark:text-neon-green" />
                ) : (
                  <MdNotificationsOff className="w-5 h-5 lg:w-6 lg:h-6 text-gray-500 dark:text-gray-400" />
                )}
                {notificationsEnabled && (
                  <span className="
                    absolute -top-1 -right-1
                    w-2 h-2
                    rounded-full
                    bg-neon-green
                    animate-pulse
                  " />
                )}
              </motion.button>

              {/* Profile Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/profile')}
                className="
                  flex items-center gap-2
                  px-3 py-2
                  rounded-lg
                  dark:text-gray-300
                  text-gray-700
                  hover:dark:text-theme
                  hover:text-theme
                  transition-colors
                "
              >
                {profile?.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt="Profile" 
                    className="w-8 h-8 rounded-full object-cover border-2 border-theme"
                  />
                ) : (
                  <MdPerson className="w-4 h-4 lg:w-5 lg:h-5" />
                )}
                <span className="hidden lg:inline text-sm">{profile?.username || user?.email?.split('@')[0]}</span>
              </motion.button>

              {/* Theme Toggle */}
              <div className="flex items-center">
                <ThemeToggle />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 rounded-lg dark:text-white text-gray-900"
            >
              {showMobileMenu ? <MdClose className="w-6 h-6" /> : <MdMenu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 space-y-2 border-t dark:border-dark-hover border-gray-200"
            >
              <button
                onClick={() => {
                  navigate('/calendar');
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 rounded-lg dark:text-gray-300 text-gray-700"
              >
                <MdCalendarToday className="w-5 h-5" />
                Calendar
              </button>
              <button
                onClick={() => {
                  navigate('/archive');
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 rounded-lg dark:text-gray-300 text-gray-700"
              >
                <MdArchive className="w-5 h-5" />
                Archive
              </button>
              <button
                onClick={() => {
                  navigate('/profile');
                  setShowMobileMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 rounded-lg dark:text-gray-300 text-gray-700"
              >
                <MdPerson className="w-5 h-5" />
                Profile
              </button>
              <div className="px-4 py-2 flex items-center gap-4">
                <button
                  onClick={toggleNotifications}
                  className="
                    p-2
                    rounded-lg
                    dark:text-gray-300
                    text-gray-700
                    hover:dark:text-neon-yellow
                    hover:text-yellow-600
                    transition-colors
                    relative
                    flex items-center gap-2
                  "
                  title={notificationPermission !== 'granted' 
                    ? 'Enable notifications'
                    : notificationsEnabled 
                      ? 'Notifications enabled - Click to disable' 
                      : 'Notifications disabled - Click to enable'
                  }
                >
                  {notificationPermission === 'granted' && notificationsEnabled ? (
                    <MdNotifications className="w-6 h-6 text-neon-green dark:text-neon-green" />
                  ) : (
                    <MdNotificationsOff className="w-6 h-6" />
                  )}
                  <span>
                    {notificationPermission !== 'granted' ? 'Enable Notifications' : 
                      notificationsEnabled ? 'Notifications On' : 'Notifications Off'}
                  </span>
                </button>
                <ThemeToggle />
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        {/* Header with View Switcher and New Task Button */}
        <div className="flex items-center justify-between mb-6 sm:mb-8 flex-wrap gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
              My Tasks
            </h1>
            <p className="text-sm sm:text-base" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
              {filteredTasks.length} {searchQuery ? 'matching' : 'total'} task{filteredTasks.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
            {/* View Mode Switcher - Always visible */}
            <div className="flex items-center gap-1 p-1 dark:bg-dark-surface bg-light-surface rounded-lg border-2 dark:border-dark-hover border-gray-300 flex-1 sm:flex-initial">
              <button
                onClick={() => setViewMode('list')}
                className={`
                  p-1.5 sm:p-2 rounded-md transition-all
                  ${viewMode === 'list'
                    ? 'dark:bg-theme/20 bg-theme/10 dark:text-theme text-theme shadow-md'
                    : 'dark:text-gray-400 text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-hover'
                  }
                `}
                title="List view"
              >
                <MdViewList className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`
                  p-1.5 sm:p-2 rounded-md transition-all
                  ${viewMode === 'grid'
                    ? 'dark:bg-theme/20 bg-theme/10 dark:text-theme text-theme shadow-md'
                    : 'dark:text-gray-400 text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-hover'
                  }
                `}
                title="Grid view"
              >
                <MdViewModule className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`
                  p-1.5 sm:p-2 rounded-md transition-all
                  ${viewMode === 'kanban'
                    ? 'dark:bg-theme/20 bg-theme/10 dark:text-theme text-theme shadow-md'
                    : 'dark:text-gray-400 text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-hover'
                  }
                `}
                title="Kanban board"
              >
                <MdViewKanban className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
            
            {/* New Task Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setEditingTask(null);
                setShowForm(!showForm);
              }}
              className="
                flex items-center justify-center gap-2
                px-3 sm:px-6 py-2 sm:py-3
                rounded-lg
                border-2
                dark:border-theme dark:bg-theme/10 dark:text-theme
                border-theme bg-theme text-white
                font-semibold
                shadow-lg
                dark:hover:bg-theme/20 hover:opacity-90
                focus:outline-none
                focus:ring-2
                focus:ring-theme
                transition-all
                text-sm sm:text-base
                whitespace-nowrap
              "
            >
              {showForm ? <MdClose className="w-4 h-4 sm:w-5 sm:h-5" /> : <MdAdd className="w-4 h-4 sm:w-5 sm:h-5" />}
              <span className="hidden sm:inline">{showForm ? 'Cancel' : 'New Task'}</span>
            </motion.button>
          </div>
        </div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="relative max-w-xl">
            <MdSearch 
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
              style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks by title, description, or tags..."
              className="
                w-full
                pl-12 pr-4 py-3
                rounded-lg
                border-2
                transition-all
                focus:outline-none
                focus:ring-2
                focus:border-transparent
              "
              style={{
                backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                color: theme === 'dark' ? '#ffffff' : '#111827'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <MdClose className="w-5 h-5" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }} />
              </button>
            )}
          </div>
        </motion.div>

        {/* Task Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-8"
          >
            <TaskForm
              initialTask={editingTask || undefined}
              onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
              onCancel={handleCancelForm}
            />
          </motion.div>
        )}

        {/* Task View - No view mode switcher needed in TaskList since it's in header */}
        {viewMode === 'kanban' ? (
          <KanbanBoard
            tasks={filteredTasks}
            onUpdateStatus={handleUpdateStatus}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ) : (
          <div className={`
            ${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}
          `}>
            {filteredTasks.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-lg" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                  {searchQuery ? 'No tasks match your search' : 'No tasks yet'}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-4 px-4 py-2 rounded-lg font-semibold transition-all"
                    style={{
                      backgroundColor: 'var(--color-theme)',
                      color: '#ffffff'
                    }}
                  >
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              filteredTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onArchive={handleArchive}
                />
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;

